import React, { useRef, useState, memo, useEffect } from 'react'
import { motion, useMotionValue, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { FlexibleCard } from '@/features/workstation/components/search-result-sources/generic-search-result'
import { ExpandedImage } from '../../expanded-cards/expanded-image'
import { ExpandedSpreadsheet } from '../../expanded-cards/expanded-spreadsheet'
import { CardProps } from '../../types'
import { closeSpring, openSpring } from '../animations'
import { Overlay } from '../card-overlay'
import { CardHeader } from './card-headers'
import { CardNavigation } from './card-navigation'
import { DocumentContent } from './document-content'
import {
  getFileType,
  isSpreadsheetFile,
  isVideoFile,
  isViewableFile,
} from './file-type-utils'
import { SpreadsheetContent } from './spreadsheet-content'
import { useCardData } from './use-card-data'
import { useCardNavigation } from './use-card-navigation'
import { VideoContent } from './video-content'

export const NewSourceCardItem = memo(
  ({
    id,
    title,
    category,
    isSelected,
    onClose,
    onSelect,
    pointOfInterest = 0,
    backgroundColor,
    imageUrl,
    content,
    groupedCards,
    attributes,
    className,
  }: CardProps) => {
    const zIndex = useMotionValue(isSelected ? 100 : 0)
    const cardRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLLIElement>(null)
    const [isImageExpanded, setIsImageExpanded] = useState(false)
    const sourceId = attributes?.sourceId
    const sourceLink = attributes?.sourceLink

    const {
      currentCardIndex,
      isLoading,
      navigateToPrevCard,
      navigateToNextCard,
    } = useCardNavigation(groupedCards, isSelected)

    const currentCard =
      groupedCards && groupedCards.length > 0
        ? groupedCards[currentCardIndex]
        : null

    const { displayContent, displayTitle, displayAttributes, relevanceValue } =
      useCardData(isSelected, currentCard, {
        title,
        category,
        content,
        attributes,
        pointOfInterest,
      })

    const fileType = getFileType(displayAttributes)
    const isVideo = isVideoFile(fileType)
    const isSpreadsheet = isSpreadsheetFile(title, fileType)
    const isViewable = isViewableFile(fileType)
    const sourceUrl = displayAttributes?.publicSourceUrl || imageUrl || ''

    const handleCardClick = () => {
      if (!isSelected) {
        onSelect(id, sourceId)
      }
    }

    const handleCardContentClick = (e: React.MouseEvent) => {
      if (isSelected) {
        e.stopPropagation()
      }
    }

    const handleImageExpand = () => {
      setIsImageExpanded(true)
    }

    const handleImageClose = () => {
      setIsImageExpanded(false)
    }

    useEffect(() => {
      if (!isSelected) return

      const handleClickOutside = (event: MouseEvent) => {
        if (isImageExpanded) return

        if (
          cardRef.current &&
          !cardRef.current.contains(event.target as Node)
        ) {
          onClose()
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [isSelected, onClose, isImageExpanded])

    function checkZIndex(latest: { scaleX: number }) {
      if (isSelected) {
        zIndex.set(100)
      } else if (!isSelected && latest.scaleX < 1.01) {
        zIndex.set(0)
      }
    }

    const renderContent = () => {
      if (isVideo) {
        return (
          <VideoContent
            title={title}
            segmentTimestamps={displayAttributes?.segmentTimestamps}
            groupedCards={groupedCards}
            currentCardIndex={currentCardIndex}
            relevanceValue={relevanceValue}
          />
        )
      }

      if (isSpreadsheet) {
        return <SpreadsheetContent onExpand={handleImageExpand} />
      }

      return (
        <DocumentContent
          title={displayTitle}
          content={displayContent}
          summary={displayAttributes?.summary}
          sourceUrl={sourceUrl}
        />
      )
    }

    return (
      <li
        ref={containerRef}
        className={cn(
          `relative mx-auto w-full max-w-[600px] ${isSelected ? 'z-[9999]' : ''}`,
          className
        )}
      >
        {isSelected && <Overlay onClose={onClose} />}

        <AnimatePresence>
          {isImageExpanded && isViewable && (
            <ExpandedImage
              src={sourceUrl}
              alt={displayTitle}
              onClose={handleImageClose}
            />
          )}
          {isImageExpanded && sourceUrl && isSpreadsheet && (
            <ExpandedSpreadsheet
              src={sourceUrl}
              title={displayTitle}
              onClose={handleImageClose}
            />
          )}
        </AnimatePresence>

        <div
          className={`${isSelected ? 'fixed bottom-0 left-0 right-0 top-0 z-[9998] flex items-center justify-center' : 'pointer-events-none relative flex w-full items-center'}`}
          style={{ perspective: 1200 }}
        >
          <motion.div
            ref={cardRef}
            className={`pointer-events-auto relative rounded-md ${isSelected && 'bg-card'} sm:rounded-[20px] ${isSelected ? 'mx-auto my-auto h-auto max-h-[90vh] w-[95%] md:w-[85%] lg:w-[700px]' : 'h-full w-full'}`}
            style={{ zIndex }}
            layout
            transition={isSelected ? openSpring : closeSpring}
            onClick={(e) => {
              e.stopPropagation()
              handleCardContentClick(e)
            }}
            onUpdate={checkZIndex}
          >
            {!isSelected && (
              <FlexibleCard
                type='document'
                title={displayTitle}
                content={content}
                relevanceValue={relevanceValue}
                sourceLink={sourceLink}
                handleCardClick={handleCardClick}
                groupedCards={groupedCards}
                segmentTimestamps={displayAttributes?.segmentTimestamps || []}
              />
            )}

            {isSelected && (
              <div className='flex h-[90vh] max-h-[90vh] flex-col'>
                <CardHeader
                  title={displayTitle}
                  backgroundColor={backgroundColor}
                  sourceUrl={sourceUrl}
                  fileType={fileType}
                  relevanceValue={relevanceValue}
                  groupedCards={groupedCards}
                  currentCardIndex={currentCardIndex}
                  streamLinkWithTimestamp={
                    displayAttributes?.streamLinkWithTimestamp
                  }
                  onClose={onClose}
                  onImageExpand={handleImageExpand}
                />

                <div className='relative flex-grow overflow-y-auto p-6 pb-20 sm:p-8 sm:pb-24'>
                  {isLoading && (
                    <div className='absolute inset-0 z-10 flex items-center justify-center bg-background/60'>
                      <div className='h-6 w-6 animate-spin rounded-full border border-primary/30 border-r-primary/20 border-t-primary'></div>
                    </div>
                  )}

                  <AnimatePresence mode='wait'>
                    <motion.div
                      key={currentCardIndex}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {renderContent()}
                    </motion.div>
                  </AnimatePresence>
                </div>

                {groupedCards && groupedCards.length > 1 && (
                  <CardNavigation
                    currentIndex={currentCardIndex}
                    totalCards={groupedCards.length}
                    isLoading={isLoading}
                    onPrevious={navigateToPrevCard}
                    onNext={navigateToNextCard}
                  />
                )}
              </div>
            )}
          </motion.div>
        </div>
      </li>
    )
  },
  (prev, next) => prev.isSelected === next.isSelected
)
